import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import SocialButton from '@/components/common/SocialButton';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InputLabel } from '../ui/InputLabel';

type FormValues = {
  email: string;
  password: string;
};

export default function LoginForm({
  onSubmit,
  onGoogle,
  loading,
  formError,
}: Readonly<{
  onSubmit?: (data: FormValues) => Promise<void> | void;
  onGoogle?: () => Promise<void> | void;
  loading?: boolean;
  formError?: string | null;
}>) {
  const { t } = useTranslation(['auth', 'common']);
  const schema = useMemo(
    () =>
      z.object({
        email: z.email(t('errors.email')),
        password: z.string().min(1, t('common:errors.fieldRequired')),
      }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
    mode: 'onChange',
  });

  const isValid = form.formState.isValid;

  return (
    <form onSubmit={form.handleSubmit(async (data) => onSubmit && (await onSubmit(data)))}>
      <div className="space-y-4">
        <div className="space-y-1">
          <Controller
            name="email"
            control={form.control}
            render={({ field }) => (
              <InputLabel
                label={t('auth:email')}
                id="email"
                type="email"
                className="border-input-inverse bg-transparent"
                classNameLabel="text-tertiary-inverse"
                classNameInput="text-white"
                value={field.value}
                onChange={field.onChange}
                autoComplete={'username'}
              />
            )}
          />

          {form.formState.errors.email && (
            <p className="text-xs text-error-400">{form.formState.errors.email.message}</p>
          )}
        </div>

        <div className="space-y-1">
          <Controller
            name="password"
            control={form.control}
            render={({ field }) => (
              <InputLabel
                label={t('auth:password')}
                id="password"
                type="password"
                className="border-input-inverse bg-transparent"
                classNameLabel="text-tertiary-inverse"
                classNameInput="text-white"
                value={field.value}
                onChange={field.onChange}
                autoComplete={'current-password'}
              />
            )}
          />
          {form.formState.errors.password && (
            <p className="text-xs text-error-400">{form.formState.errors.password.message}</p>
          )}
          {formError && <p className="text-xs text-error-400">{formError}</p>}
        </div>
      </div>

      <div className="mt-6">
        <Button
          type="submit"
          className="w-full h-11 rounded-full mt-6 text-base disabled:bg-button-primary-disable-inverse disabled:text-disable"
          disabled={!isValid || loading}
        >
          {t('auth:submit')}
        </Button>

        <Link
          to="/forgot-password"
          className="mt-6 text-button-secondary text-center font-medium flex items-center justify-center"
        >
          {t('auth:forgot')}
        </Link>
      </div>

      <div className="my-16">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Separator className="flex-1 h-px bg-card-inverse" />
            <span className="text-sm text-secondary-inverse whitespace-nowrap">
              {t('auth:orWith')}
            </span>
            <Separator className="flex-1 h-px bg-card-inverse" />
          </div>

          <SocialButton provider="google" onClick={onGoogle} disabled={loading} />
        </div>
      </div>

      <p className=" text-xs text-disable mt-8">
        {t('auth:recaptcha')}{' '}
        <Link
          className="underline text-disable "
          to="https://www.welya.io/politique-de-confidentialité"
          target="_blank"
        >
          {t('auth:confidentiality')}
        </Link>{' '}
        {t('auth:apply')}{' '}
        <Link
          className="underline text-disable"
          to="https://policies.google.com/terms"
          target="_blank"
        >
          {t('auth:terms')}
        </Link>
      </p>
    </form>
  );
}
